// Barnard College: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Barnard's Common Data Set 2025-2026: C1 applicants, admits and enrollees
//    for Fall 2025 (the C1 residency table is left at zero, so there are no
//    residency rows), C8 score deadline, C21 early decision, G1/G5 costs for
//    2026-2027. Quirk: C21 labels its early decision counts (1,437 applied,
//    476 admitted) "For the Fall 2024 entering class", so they are left out
//    rather than shown beside the fall 2025 totals. The C8 policy boxes are
//    blank in the PDF; the policy comes from Barnard's admissions pages.
//  - barnard.edu admissions: Early Decision and Regular Decision (dates for
//    the 2026-2027 rounds), The Application Process, Standardized Testing,
//    and the Temporary Test-Optional FAQ.
//  - College Scorecard API, school id 189097 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Barnard Common Data Set 2025-2026',
        url: 'https://barnard.edu/sites/default/files/2026-08/Barnard_CDS_2025-2026.pdf',
        read: '2026-10-01',
    },
    rounds: {
        label: 'Barnard Admissions, Early Decision and Regular Decision',
        url: 'https://barnard.edu/admissions/application-rounds',
        read: '2026-10-01',
    },
    process: {
        label: 'Barnard Admissions, The Application Process',
        url: 'https://barnard.edu/admissions/the-application-process',
        read: '2026-10-01',
    },
    testing: {
        label: 'Barnard Admissions, Standardized Testing',
        url: 'https://barnard.edu/admissions/testing',
        read: '2026-10-01',
    },
    testFaq: {
        label: 'Barnard Admissions, Temporary Test-Optional FAQ',
        url: 'https://barnard.edu/temporary-test-optional-policy-faq',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?189097-Barnard-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'barnard-college',
    kbId: 'barnard_college',
    name: 'Barnard College',
    shortName: 'Barnard',
    location: 'New York, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // The KB holds no sourced school colors for Barnard, so no theme.
    branding: null,

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 10426,
        admitted: 1048,
        enrolled: 702,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Barnard\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set gives early decision counts only for an earlier class, so this page shows no early decision acceptance rate. Barnard says its review is the same in both rounds and that it has historically filled 40 to 45 percent of the first-year class from early decision.',
        source: 'rounds',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Late March', binding: false },
        ],
        note: 'The application, essay and short answer questions are due by 11:59 pm your local time. If you send test scores, self-report them by November 20 for Early Decision or February 28 for Regular Decision. The reply date is May 1.',
        source: 'process',
        binding: 'Early Decision is binding: if you are admitted, you agree to attend Barnard and withdraw all other applications, and you sign an Early Decision Agreement. If you are deferred, you are reviewed again in Regular Decision.',
        bindingSource: 'rounds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Barnard is SAT and ACT test-optional for students applying for 2027 entry. You may apply with or without scores, and nothing else is asked for in their place. If you send scores, Barnard superscores both the SAT and the ACT, and you self-report them; admitted students who enroll send official scores.',
        source: 'testing',
        cdsLine: 'The Common Data Set gives January 6 as the latest date for SAT or ACT scores to arrive; Barnard\'s own pages give the later self-report dates above.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Barnard calls its test-optional policy temporary and has set it only through the 2027 entry cycle. It has not said whether students entering in fall 2028 must send SAT or ACT scores.', source: 'testFaq' },
        ],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73120 },
            { item: 'Required fees', amount: 2472 },
            { item: 'Housing (on campus)', amount: 14054 },
            { item: 'Food (on-campus meal plan)', amount: 9204 },
            { item: 'Books and supplies', amount: 1200 },
            { item: 'Transportation', amount: 1070 },
            { item: 'Other expenses', amount: 1830 },
        ],
        total: 102950,
        note: 'For a full-time first-year student living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 28930,
            byIncome: [
                { income: '$0 to $30,000', amount: 18022 },
                { income: '$30,001 to $48,000', amount: 11372 },
                { income: '$48,001 to $75,000', amount: 9628 },
                { income: '$75,001 to $110,000', amount: 26883 },
                { income: 'Over $110,000', amount: 59926 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Barnard\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
